import React, { useState, useEffect } from 'react';

const FONT = "'Outfit', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const Icon = ({ d, size = 20, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ICONS = {
    drivers: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    clients: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
    vendors: 'M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z M3 6h18 M16 10a4 4 0 0 1-8 0',
    fleet: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    members: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
    pie: ['M21.21 15.89A10 10 0 1 1 8 2.83', 'M22 12A10 10 0 0 0 12 2v10z'],
    activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
    arrow: 'M5 12h14M12 5l7 7-7 7',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
    check: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    trend: 'M23 6l-9.5 9.5-5-5L1 18',
};

/* Subtle sparkline decorations for KPI cards */
const sparkPaths = [
    'M0 24 L14 20 L28 26 L42 14 L56 18 L70 8 L84 16 L98 6 L112 12 L120 4',
    'M0 20 L14 26 L28 12 L42 18 L56 8 L70 16 L84 6 L98 14 L112 4 L120 10',
    'M0 14 L14 22 L28 10 L42 24 L56 12 L70 20 L84 8 L98 18 L112 6 L120 16',
];

/* ─── Pie Chart ─── */
function PieChart({ data, size = 150 }) {
    const total = data.reduce((s, d) => s + d.value, 0);
    if (total === 0) return (
        <div style={{ width: size, height: size, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#94a3b8' }}>No data</div>
    );
    const cx = size / 2, cy = size / 2, r = size / 2 - 6;
    let acc = 0;
    const slices = data.map(d => {
        const pct = d.value / total;
        const s = acc * 2 * Math.PI - Math.PI / 2;
        acc += pct;
        const e = acc * 2 * Math.PI - Math.PI / 2;
        return { ...d, path: `M ${cx} ${cy} L ${cx + r * Math.cos(s)} ${cy + r * Math.sin(s)} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${cx + r * Math.cos(e)} ${cy + r * Math.sin(e)} Z`, pct };
    });
    return (
        <svg width={size} height={size} style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.10))' }}>
            {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} opacity={0.92} />)}
            <circle cx={cx} cy={cy} r={r * 0.54} fill="white" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize={18} fontWeight={900} fill="#0f172a" fontFamily={FONT}>{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize={8} fontWeight={700} fill="#94a3b8" fontFamily={FONT} letterSpacing={1.5}>TOTAL</text>
        </svg>
    );
}

/* ─── HBar ─── */
function HBar({ label, value, max, color }) {
    const pct = max === 0 ? 0 : Math.min(100, (value / max) * 100);
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b', fontFamily: FONT }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: FONT }}>{value}</span>
            </div>
            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 1s cubic-bezier(0.4,0,0.2,1)' }} />
            </div>
        </div>
    );
}

export default function OperatorDashboard({ user, onNavigate }) {
    const [stats, setStats] = useState({ drivers: 0, clients: 0, vendors: 0, fleet: 0, members: 0, routes: 0, passengers: 0 });
    const [tripCounts, setTripCounts] = useState({ Scheduled: 0, Ongoing: 0, Completed: 0 });
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [headerBg, setHeaderBg] = useState('linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)');
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    useEffect(() => {
        if (!operatorId) return;
        (async () => {
            try {
                const [driversRes, clientsRes, vendorsRes, fleetRes, operatorRes, tripsRes, routesRes, subRes, membersRes, passengersRes] = await Promise.all([
                    fetch(`/api/drivers/${operatorId}`),
                    fetch(`/api/clients/${operatorId}`),
                    fetch(`/api/vendors/${operatorId}`),
                    fetch(`/api/vehicles/${operatorId}`),
                    fetch(`/api/operators/${operatorId}`),
                    fetch(`/api/trips/${operatorId}`),
                    fetch(`/api/routes/${operatorId}`),
                    fetch(`/api/subscriptions/operator/${operatorId}`),
                    fetch(`/api/operators/${operatorId}/members`),
                    fetch(`/api/passengers/${operatorId}`)
                ]);
                const [drivers, clients, vendors, fleet, operator, trips, routes, subscriptions, members, passengers] = await Promise.all([
                    driversRes.json(), clientsRes.json(), vendorsRes.json(), fleetRes.json(), operatorRes.json(), tripsRes.json(), routesRes.json(), subRes.json(), membersRes.json(), passengersRes.json()
                ]);
                setStats({
                    drivers: Array.isArray(drivers) ? drivers.length : 0,
                    clients: Array.isArray(clients) ? clients.length : 0,
                    vendors: Array.isArray(vendors) ? vendors.length : 0,
                    fleet: Array.isArray(fleet) ? fleet.length : 0,
                    members: Array.isArray(members) ? members.length : 0,
                    routes: Array.isArray(routes) ? routes.length : 0,
                    passengers: Array.isArray(passengers) ? passengers.length : 0,
                });
                if (Array.isArray(subscriptions)) {
                    const activeSub = subscriptions.find(s => s.status === 'Active');
                    if (activeSub) setSubscription(activeSub);
                }
                if (Array.isArray(trips)) {
                    const counts = { Scheduled: 0, Ongoing: 0, Completed: 0 };
                    trips.forEach(t => {
                        const s = t.status || 'Scheduled';
                        if (counts[s] !== undefined) counts[s]++;
                    });
                    setTripCounts(counts);
                }
            } catch (err) {
                console.error('Dashboard fetch error:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, [operatorId]);

    const totalTrips = tripCounts.Scheduled + tripCounts.Ongoing + tripCounts.Completed;
    const networkTotal = Math.max(stats.drivers + stats.clients + stats.vendors + stats.fleet + stats.members, 1);

    const tripCards = [
        {
            status: 'Scheduled',
            count: tripCounts.Scheduled,
            icon: ICONS.clock,
            desc: 'Upcoming trips ready to go',
            color: '#2563eb',
            accentGrad: 'linear-gradient(90deg, #2563eb, #60a5fa)',
            iconBg: '#eff6ff',
            badgeBg: '#eff6ff',
            badgeColor: '#2563eb',
        },
        {
            status: 'Ongoing',
            count: tripCounts.Ongoing,
            icon: ICONS.bolt,
            desc: 'Currently in progress',
            color: '#0284c7',
            accentGrad: 'linear-gradient(90deg, #0284c7, #38bdf8)',
            iconBg: '#f0f9ff',
            badgeBg: '#f0f9ff',
            badgeColor: '#0284c7',
        },
        {
            status: 'Completed',
            count: tripCounts.Completed,
            icon: ICONS.check,
            desc: 'Successfully finished',
            color: '#1e40af',
            accentGrad: 'linear-gradient(90deg, #1e40af, #3b82f6)',
            iconBg: '#eff6ff',
            badgeBg: '#eff6ff',
            badgeColor: '#1e40af',
        },
    ];

    const kpis = [
        { label: 'Total Drivers', value: stats.drivers, icon: ICONS.drivers, color: '#2563eb', bg: '#eff6ff' },
        { label: 'Active Clients', value: stats.clients, icon: ICONS.clients, color: '#1d4ed8', bg: '#eff6ff' },
        { label: 'Vendors & Partners', value: stats.vendors, icon: ICONS.vendors, color: '#0284c7', bg: '#f0f9ff' },
        { label: 'Managed Fleet', value: stats.fleet, icon: ICONS.fleet, color: '#1e40af', bg: '#eff6ff' },
        { label: 'Team Members', value: stats.members, icon: ICONS.members, color: '#3b82f6', bg: '#eff6ff' },
    ];

    const pieData = [
        { label: 'Drivers', value: stats.drivers, color: '#2563eb' },
        { label: 'Clients', value: stats.clients, color: '#3b82f6' },
        { label: 'Vendors', value: stats.vendors, color: '#1d4ed8' },
        { label: 'Fleet', value: stats.fleet, color: '#1e40af' },
        { label: 'Members', value: stats.members, color: '#60a5fa' },
    ];

    const efficiency = [
        { label: 'Fleet Utilisation', value: stats.fleet > 0 ? Math.min(100, Math.round((stats.drivers / Math.max(stats.fleet, 1)) * 100)) : 0, color: '#2563eb' },
        { label: 'Driver Coverage', value: stats.clients > 0 ? Math.min(100, Math.round((stats.drivers / Math.max(stats.clients, 1)) * 100)) : 0, color: '#1d4ed8' },
        { label: 'Vendor Diversity', value: stats.vendors > 0 ? Math.min(100, Math.round((stats.vendors / Math.max(stats.fleet, 1)) * 100)) : 0, color: '#0284c7' },
        { label: 'Team Ratio', value: stats.members > 0 ? Math.min(100, Math.round((stats.members / Math.max(stats.drivers + stats.clients, 1)) * 100)) : 0, color: '#3b82f6' },
    ];

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: FONT }}>
            <style>{`
                @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.7} 70%{transform:scale(1.7);opacity:0} 100%{transform:scale(1);opacity:0} }
                .trip-card { cursor: pointer; transition: transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s ease; }
                .trip-card:hover { transform: translateY(-5px); box-shadow: 0 12px 30px -6px rgba(0,0,0,0.1) !important; }
                .kpi-card { transition: transform 0.2s ease, box-shadow 0.2s ease; animation: fadeUp 0.4s ease both; }
                .kpi-card:hover { transform: translateY(-4px); box-shadow: 0 14px 28px -6px rgba(0,0,0,0.08) !important; }
                .dash-section { animation: fadeUp 0.45s ease both; }
                .view-all-chip { transition: all 0.2s ease; }
                .view-all-chip:hover { transform: translateX(2px); filter: brightness(0.96); }
            `}</style>

            {/* ── Deep Navy Blue Hero Header ── */}
            <div style={{
                background: 'linear-gradient(135deg, #0b1e3b 0%, #112d59 52%, #183d73 100%)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                padding: '36px 44px 105px',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 14px 35px -8px rgba(11, 30, 59, 0.35)'
            }}>
                {/* Luminous accents */}
                <div style={{ position: 'absolute', top: -70, right: -40, width: 340, height: 340, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.22) 0%, transparent 65%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: -90, left: 60, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14, 165, 233, 0.16) 0%, transparent 65%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: 30, left: '42%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />

                <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
                    <div>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 12px',
                            borderRadius: 9999,
                            background: 'rgba(255, 255, 255, 0.12)',
                            backdropFilter: 'blur(8px)',
                            color: '#93c5fd',
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            marginBottom: 10
                        }}>
                            Analytics Overview
                        </div>
                        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.15, textShadow: '0 2px 6px rgba(0,0,0,0.25)' }}>
                            Operational Dashboard
                        </h1>
                        <p style={{ color: '#cbd5e1', fontSize: 14, margin: '8px 0 0', fontWeight: 500 }}>
                            Real-time insights across your logistics network
                        </p>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 9,
                        padding: '10px 18px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 12,
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#ffffff',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ position: 'relative', width: 8, height: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', position: 'absolute' }} />
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', position: 'absolute', animation: 'pulse-ring 1.8s ease infinite' }} />
                        </div>
                        Live Data
                    </div>
                </div>

                {/* Trip Summary Frosted Glass Chips */}
                <div style={{ position: 'relative', zIndex: 2, display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Scheduled', count: tripCounts.Scheduled, bg: 'rgba(59, 130, 246, 0.18)', border: 'rgba(147, 197, 253, 0.3)', text: '#bfdbfe' },
                        { label: 'Ongoing', count: tripCounts.Ongoing, bg: 'rgba(16, 185, 129, 0.18)', border: 'rgba(110, 231, 183, 0.3)', text: '#a7f3d0' },
                        { label: 'Completed', count: tripCounts.Completed, bg: 'rgba(129, 140, 248, 0.18)', border: 'rgba(199, 210, 254, 0.3)', text: '#c7d2fe' },
                    ].map((item, i) => (
                        <div
                            key={item.label}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                background: item.bg,
                                border: `1px solid ${item.border}`,
                                backdropFilter: 'blur(8px)',
                                padding: '6px 14px',
                                borderRadius: 10,
                                color: '#ffffff',
                                animationDelay: `${i * 0.08}s`
                            }}
                        >
                            <span style={{ fontSize: 12.5, fontWeight: 600, color: item.text }}>{item.label}:</span>
                            <span style={{ fontSize: 16, fontWeight: 800, color: '#ffffff' }}>{item.count}</span>
                        </div>
                    ))}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: 'rgba(255, 255, 255, 0.12)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(8px)',
                        padding: '6px 16px',
                        borderRadius: 10
                    }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#e2e8f0' }}>Total Trips:</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#ffffff' }}>{totalTrips}</span>
                    </div>
                </div>
            </div>

            {/* ── Main Content Area ── */}
            <div style={{ padding: '0 44px 48px', marginTop: -65, position: 'relative', zIndex: 10 }}>

                {/* Trip Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 26 }}>
                    {tripCards.map((tc, i) => (
                        <div
                            key={tc.status}
                            className="trip-card dash-section"
                            onClick={() => onNavigate && onNavigate('operator-trip-list', tc.status)}
                            style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: 18,
                                padding: '24px 26px',
                                boxShadow: '0 4px 20px -4px rgba(0,0,0,0.06)',
                                position: 'relative',
                                overflow: 'hidden',
                                animationDelay: `${i * 0.08}s`
                            }}
                        >
                            {/* Top accent line */}
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: tc.accentGrad }} />

                            <div style={{ position: 'relative', zIndex: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                                    <div style={{ width: 44, height: 44, borderRadius: 12, background: tc.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon d={tc.icon} size={20} color={tc.color} />
                                    </div>
                                    <div className="view-all-chip" style={{ display: 'flex', alignItems: 'center', gap: 5, background: tc.badgeBg, borderRadius: 20, padding: '5px 12px', fontSize: 11.5, fontWeight: 700, color: tc.badgeColor }}>
                                        View All <Icon d="M5 12h14M12 5l7 7-7 7" size={12} color={tc.badgeColor} />
                                    </div>
                                </div>
                                <div style={{ fontSize: 44, fontWeight: 800, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.03em', marginBottom: 6 }}>
                                    {loading ? '—' : tc.count}
                                </div>
                                <div style={{ fontSize: 14.5, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
                                    {tc.status} Trips
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{tc.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* KPI Metric Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 26 }}>
                    {loading
                        ? [1, 2, 3, 4, 5].map(i => (
                            <div key={i} style={{ background: 'white', borderRadius: 16, padding: 20, height: 115, background: 'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                        ))
                        : kpis.map((k, i) => (
                            <div
                                key={k.label}
                                className="kpi-card"
                                style={{
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 16,
                                    padding: '20px 20px 18px',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                                    animationDelay: `${0.15 + i * 0.05}s`,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                <div style={{ position: 'relative', zIndex: 2 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                                        <Icon d={k.icon} size={20} color={k.color} />
                                    </div>
                                    <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{k.value}</div>
                                    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b', marginTop: 6 }}>{k.label}</div>
                                </div>
                            </div>
                        ))
                    }
                </div>

                {/* Subscription Details Row */}
                {subscription && subscription.planId && (
                    <div style={{ marginBottom: 26 }}>
                        <div className="dash-section" style={{ background: 'white', borderRadius: 18, border: '1px solid #e2e8f0', padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.03)', animationDelay: '0.25s' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 38, height: 38, borderRadius: 11, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" size={17} color="#7c3aed" />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                                            Current Subscription: <span style={{ color: '#2563eb' }}>{subscription.planId.name}</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, marginTop: 2 }}>
                                            Valid until: {new Date(subscription.nextBillingDate || subscription.startDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                                    Active Plan
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                                {[
                                    { label: 'Drivers Limit', current: stats.drivers, limit: subscription.planId.driversLimit !== undefined ? subscription.planId.driversLimit : subscription.planId.operatorsLimit || 'Unlimited', color: '#2563eb' },
                                    { label: 'Fleet Limit', current: stats.fleet, limit: subscription.planId.fleetLimit || 'Unlimited', color: '#1d4ed8' },
                                    { label: 'Users Limit', current: stats.members, limit: subscription.planId.usersLimit || 'Unlimited', color: '#0284c7' },
                                    { label: 'Routes Limit', current: stats.routes, limit: subscription.planId.routesLimit || 'Unlimited', color: '#1e40af' },
                                    { label: 'Passenger Limit', current: stats.passengers, limit: subscription.planId.passengerLimit || 'Unlimited', color: '#3b82f6' },
                                    { label: 'Clients Limit', current: stats.clients, limit: subscription.planId.clientsLimit || 'Unlimited', color: '#60a5fa' }
                                ].map(item => {
                                    const isUnlimited = item.limit === 'Unlimited' || item.limit === -1 || item.limit === '-1' || item.limit === null;
                                    const limitVal = isUnlimited ? Infinity : Number(item.limit);
                                    const displayLimit = isUnlimited ? '∞' : limitVal;
                                    const pct = isUnlimited ? 0 : Math.min(100, (item.current / limitVal) * 100);
                                    
                                    return (
                                        <div key={item.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '15px 16px', border: '1px solid #f1f5f9' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{item.label}</span>
                                                <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                                                    {item.current} / <span style={{ color: '#94a3b8' }}>{displayLimit}</span>
                                                </span>
                                            </div>
                                            {!isUnlimited && (
                                                <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', width: `${pct}%`, background: item.color, borderRadius: 99, transition: 'width 0.8s ease' }} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Middle Row: Network Distribution & Strength */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 26 }}>

                    {/* Network Distribution */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 18, border: '1px solid #e2e8f0', padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.03)', animationDelay: '0.3s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 11, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={ICONS.pie} size={17} color="#2563eb" />
                            </div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Network Distribution</div>
                                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>All entities breakdown</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                            <PieChart data={pieData} size={160} />
                            <div style={{ flex: 1 }}>
                                {pieData.map(d => (
                                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{d.label}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{d.value}</span>
                                            <span style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', background: '#f1f5f9', padding: '2px 7px', borderRadius: 6 }}>
                                                {networkTotal > 0 ? Math.round((d.value / networkTotal) * 100) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Network Strength */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 18, border: '1px solid #e2e8f0', padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.03)', animationDelay: '0.35s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 11, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={ICONS.activity} size={17} color="#2563eb" />
                            </div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Network Strength</div>
                                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Comparative unit count</div>
                            </div>
                        </div>
                        {[
                            { label: 'Drivers', value: stats.drivers, color: '#2563eb' },
                            { label: 'Clients', value: stats.clients, color: '#3b82f6' },
                            { label: 'Vendors', value: stats.vendors, color: '#1d4ed8' },
                            { label: 'Fleet Vehicles', value: stats.fleet, color: '#1e40af' },
                            { label: 'Team Members', value: stats.members, color: '#60a5fa' },
                        ].map(d => <HBar key={d.label} {...d} max={networkTotal} />)}
                    </div>
                </div>

                {/* Bottom Row: Efficiency & Health */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20 }}>

                    {/* Efficiency Metrics */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 18, border: '1px solid #e2e8f0', padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.03)', animationDelay: '0.4s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 11, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={ICONS.zap} size={17} color="#2563eb" />
                            </div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Efficiency Metrics</div>
                                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Calculated from live data</div>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            {efficiency.map(m => (
                                <div key={m.label} style={{ background: '#f8fafc', borderRadius: 14, padding: '16px 18px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>{m.label}</div>
                                    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, marginBottom: 10, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${m.value}%`, background: m.color, borderRadius: 99, transition: 'width 0.8s ease' }} />
                                    </div>
                                    <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em' }}>
                                        {m.value}<span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Platform Health */}
                    <div className="dash-section" style={{ background: 'white', borderRadius: 18, padding: '24px 28px', color: '#0f172a', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.03)', position: 'relative', overflow: 'hidden', animationDelay: '0.45s' }}>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <div style={{
                                display: 'inline-block',
                                fontSize: 11,
                                fontWeight: 800,
                                color: '#2563eb',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                marginBottom: 6
                            }}>
                                Platform Health
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 3 }}>Network Summary</div>
                            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 22 }}>Across all connected entities</div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {[
                                    { label: 'Total Entities', value: networkTotal, color: '#2563eb' },
                                    { label: 'Fleet Units', value: stats.fleet, color: '#1e40af' },
                                    { label: 'Human Resources', value: stats.drivers + stats.members, color: '#1d4ed8' },
                                    { label: 'Client Base', value: stats.clients + stats.vendors, color: '#0284c7' },
                                ].map(item => (
                                    <div key={item.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 16px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{item.label}</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: item.color, letterSpacing: '-0.03em' }}>{item.value}</div>
                                    </div>
                                ))}
                            </div>
                            
                            <div style={{ marginTop: 18, padding: '10px 14px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 9 }}>
                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563eb', boxShadow: '0 0 0 3px rgba(37,99,235,0.2)', flexShrink: 0 }} />
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>All systems operational · {totalTrips} trips tracked</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
